import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import config from 'config'
import { Button, Label } from 'design-system'
import { cn } from 'design-system/lib/utils'
import { ImageIcon, Upload, X } from 'lucide-react'

export function resolveMediaUrl(path: string | null | undefined): string | null {
 if (path == null || typeof path !== 'string') return null
 const t = path.trim()
 if (!t) return null
 if (/^https?:\/\//i.test(t)) return t
 const base = config.api.API_URL.replace(/\/?api\/?$/i, '')
 return `${base}${t.startsWith('/') ? '': '/'}${t}`
}

export type ImageUploadFieldProps = {
 label?: string
 hint?: string
 /** ملف جديد مختار محلياً */
 value: File | null
 /** صورة محفوظة من السيرفر — تظهر عندما لا يوجد `value` */
 remoteUrl?: string | null
 onChange: (file: File | null) => void
 accept?: string
 maxSizeMb?: number
 disabled?: boolean
 className?: string
 objectFit?: 'cover' | 'contain'
}

export function ImageUploadField({
 label,
 hint,
 value,
 remoteUrl = null,
 onChange,
 accept = 'image/jpeg,image/png,image/gif,image/webp',
 maxSizeMb = 2,
 disabled = false,
 className,
 objectFit = 'contain',
}: ImageUploadFieldProps) {
 const { t } = useTranslation()
 const inputRef = useRef<HTMLInputElement>(null)
 const inputId = useId()
 const [blobUrl, setBlobUrl] = useState<string | null>(null)
 const [error, setError] = useState<string | null>(null)
 const [isDragging, setIsDragging] = useState(false)

 useEffect(() => {
 if (!value) {
 setBlobUrl(null)
 return
 }
 const url = URL.createObjectURL(value)
 setBlobUrl(url)
 return () => URL.revokeObjectURL(url)
 }, [value])

 const displaySrc = blobUrl ?? resolveMediaUrl(remoteUrl)
 const hasPreview = Boolean(displaySrc)

 const pickFile = useCallback(
 (file: File | null) => {
 setError(null)
 if (!file) {
 onChange(null)
 return
 }
 if (!file.type.startsWith('image/')) {
 setError(t('form.imageUpload.selectImage'))
 return
 }
 if (maxSizeMb != null && file.size > maxSizeMb * 1024 * 1024) {
 setError(t('form.imageUpload.maxSize', { max: maxSizeMb }))
 return
 }
 onChange(file)
 },
 [maxSizeMb, onChange, t]
 )

 const openPicker = () => {
 if (!disabled) inputRef.current?.click()
 }

 const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
 const f = e.target.files?.[0] ?? null
 pickFile(f)
 e.target.value = ''
 }

 const onDrop = (e: React.DragEvent) => {
 e.preventDefault()
 e.stopPropagation()
 setIsDragging(false)
 if (disabled) return
 const f = e.dataTransfer.files?.[0]
 if (f) pickFile(f)
 }

 const onDragOver = (e: React.DragEvent) => {
 e.preventDefault()
 e.stopPropagation()
 if (!disabled) setIsDragging(true)
 }

 const onDragLeave = (e: React.DragEvent) => {
 e.preventDefault()
 e.stopPropagation()
 setIsDragging(false)
 }

 const cancelNewFile = () => {
 setError(null)
 onChange(null)
 }

 return (
 <div className={cn('space-y-2', className)}>
 {label ? <Label htmlFor={inputId}>{label}</Label> : null}

 <div
 className={cn(
 'rounded-2xl border-2 border-dashed transition-colors overflow-hidden bg-muted dark:bg-muted',
 disabled && 'pointer-events-none',
 error
 ? 'border-destructive/55'
 : isDragging
 ? 'border-primary/70 bg-primary/[0.07] shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.12)]'
 : 'border-border-subtle hover:border-primary/35 hover:bg-primary/[0.03]'
 )}
 onDrop={onDrop}
 onDragOver={onDragOver}
 onDragLeave={onDragLeave}
 >
 <input
 ref={inputRef}
 id={inputId}
 type="file" accept={accept}
 className="sr-only" onChange={onInputChange}
 disabled={disabled}
 />

 {hasPreview ? (
 <div className="relative">
 <button
 type="button" className="relative block w-full text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 rounded-t-xl" onClick={openPicker}
 disabled={disabled}
 >
 <img
 src={displaySrc!}
 alt="" className={cn(
 'w-full h-[140px] sm:h-[168px] bg-muted dark:bg-muted/25',
 objectFit === 'cover'? 'object-cover': 'object-contain'
 )}
 />
 </button>
 <div className="flex flex-wrap items-center gap-2 p-3 border-t border-border-subtle bg-card">
 <Button type="button" variant="secondary" size="sm" onClick={openPicker} disabled={disabled}>
 <Upload className="h-4 w-4 me-1.5"/>
 {t('form.imageUpload.change')}
 </Button>
 {value ? (
 <Button type="button" variant="outline" size="sm" onClick={cancelNewFile} disabled={disabled}>
 <X className="h-4 w-4 me-1.5"/>
 {t('form.imageUpload.cancelNew')}
 </Button>
 ) : null}
 </div>
 </div>
 ) : (
 <button
 type="button" className="w-full flex flex-col items-center justify-center gap-3 min-h-[120px] py-6 px-4 text-center cursor-pointer bg-transparent disabled:cursor-not-allowed sm:min-h-[140px] sm:py-8" onClick={openPicker}
 disabled={disabled}
 >
 <div className={cn('rounded-full p-3.5 transition-colors', isDragging ? 'bg-blue-100' : 'bg-muted')}>
 <ImageIcon className={cn('h-8 w-8', isDragging ? 'text-primary' : 'text-muted-foreground')} />
 </div>
 <div>
 <p className="text-sm font-medium text-foreground">{t('form.imageUpload.dropzoneTitle')}</p>
 <p className="text-xs text-muted-foreground mt-1">{t('form.imageUpload.dropzoneHint')}</p>
 </div>
 </button>
 )}
 </div>

 {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
 {error ? <p className="text-xs text-destructive">{error}</p> : null}
 {value ? (
 <p className="text-xs text-muted-foreground truncate" title={value.name}>
 {t('form.imageUpload.fileLabel', { name: value.name })}
 </p>
 ) : null}
 </div>
 )
}

export default ImageUploadField
